/**
 * Device detection utilities.
 */

export interface DeviceInfo {
  type: 'mobile' | 'tablet' | 'desktop';
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouch: boolean;
}

/**
 * Detects device information from user agent.
 */
export function detectDevice(): DeviceInfo {
  const ua = navigator.userAgent;
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Detect OS
  const { os, osVersion } = detectOS(ua);

  // Detect browser
  const { browser, browserVersion } = detectBrowser(ua);

  // Detect device type
  const isMobile = /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry/i.test(ua);
  const isTablet =
    /iPad|Android(?!.*Mobile)|Tablet|PlayBook/i.test(ua) ||
    (isTouch && window.screen.width >= 768 && window.screen.width < 1024);
  const isDesktop = !isMobile && !isTablet;

  let type: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (isMobile) type = 'mobile';
  else if (isTablet) type = 'tablet';

  return {
    type,
    os,
    osVersion,
    browser,
    browserVersion,
    isMobile,
    isTablet,
    isDesktop,
    isTouch,
  };
}

/**
 * Detects operating system.
 */
function detectOS(ua: string): { os: string; osVersion: string } {
  let os = 'Unknown';
  let osVersion = '';

  if (/Windows NT 10/i.test(ua)) {
    os = 'Windows';
    osVersion = '10';
  } else if (/Windows NT 6.3/i.test(ua)) {
    os = 'Windows';
    osVersion = '8.1';
  } else if (/Windows NT 6.2/i.test(ua)) {
    os = 'Windows';
    osVersion = '8';
  } else if (/Windows NT 6.1/i.test(ua)) {
    os = 'Windows';
    osVersion = '7';
  } else if (/Mac OS X/i.test(ua)) {
    os = 'macOS';
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  } else if (/iPhone OS|iPad.*OS/i.test(ua)) {
    os = 'iOS';
    const match = ua.match(/OS (\d+_\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  } else if (/Android/i.test(ua)) {
    os = 'Android';
    const match = ua.match(/Android (\d+\.?\d*)/);
    osVersion = match ? match[1] : '';
  } else if (/Linux/i.test(ua)) {
    os = 'Linux';
  } else if (/CrOS/i.test(ua)) {
    os = 'Chrome OS';
  }

  return { os, osVersion };
}

/**
 * Detects browser.
 */
function detectBrowser(ua: string): { browser: string; browserVersion: string } {
  let browser = 'Unknown';
  let browserVersion = '';

  // Order matters - check more specific patterns first
  if (/Edge\//i.test(ua)) {
    browser = 'Edge Legacy';
    const match = ua.match(/Edge\/(\d+\.?\d*)/);
    browserVersion = match ? match[1] : '';
  } else if (/Edg\//i.test(ua)) {
    browser = 'Edge';
    const match = ua.match(/Edg\/(\d+\.?\d*)/);
    browserVersion = match ? match[1] : '';
  } else if (/OPR\//i.test(ua)) {
    browser = 'Opera';
    const match = ua.match(/OPR\/(\d+\.?\d*)/);
    browserVersion = match ? match[1] : '';
  } else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) {
    browser = 'Chrome';
    const match = ua.match(/Chrome\/(\d+\.?\d*)/);
    browserVersion = match ? match[1] : '';
  } else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) {
    browser = 'Safari';
    const match = ua.match(/Version\/(\d+\.?\d*)/);
    browserVersion = match ? match[1] : '';
  } else if (/Firefox\//i.test(ua)) {
    browser = 'Firefox';
    const match = ua.match(/Firefox\/(\d+\.?\d*)/);
    browserVersion = match ? match[1] : '';
  } else if (/MSIE|Trident/i.test(ua)) {
    browser = 'Internet Explorer';
    const match = ua.match(/(?:MSIE |rv:)(\d+\.?\d*)/);
    browserVersion = match ? match[1] : '';
  } else if (/SamsungBrowser/i.test(ua)) {
    browser = 'Samsung Browser';
    const match = ua.match(/SamsungBrowser\/(\d+\.?\d*)/);
    browserVersion = match ? match[1] : '';
  }

  return { browser, browserVersion };
}

/**
 * Gets viewport dimensions.
 */
export function getViewport(): { width: number; height: number } {
  return {
    width: window.innerWidth || document.documentElement.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight,
  };
}

/**
 * Gets screen dimensions.
 */
export function getScreen(): {
  width: number;
  height: number;
  availWidth: number;
  availHeight: number;
  orientation: string;
} {
  return {
    width: screen.width,
    height: screen.height,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    orientation:
      screen.orientation?.type ||
      (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'),
  };
}

/**
 * Checks if the device is in portrait mode.
 */
export function isPortrait(): boolean {
  return window.innerHeight > window.innerWidth;
}

/**
 * Checks if the device is in landscape mode.
 */
export function isLandscape(): boolean {
  return window.innerWidth > window.innerHeight;
}

/**
 * Gets connection info if available.
 */
export function getConnectionInfo(): {
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
} | null {
  const nav = navigator as Navigator & {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
  };

  if (!nav.connection) return null;

  return {
    effectiveType: nav.connection.effectiveType || null,
    downlink: nav.connection.downlink || null,
    rtt: nav.connection.rtt || null,
    saveData: nav.connection.saveData || false,
  };
}
